import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, Sparkles, Leaf, Layers, User, LogOut, Heart } from 'lucide-react';

const HomeScreen = () => {
  const { setActiveTab, setCurrentScreen, addToWishlist, isInWishlist, scanHistory } = useApp();
  const { user, isAuthenticated, logout, openLogin } = useAuth();
  const [forYouItems, setForYouItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch personalized recommendations
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('auth_token');
        
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/api/recommendations/for-you?limit=4`, { headers });
        const data = await response.json();
        
        if (data.recommendations) {
          setForYouItems(data.recommendations);
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        // Use fallback hardcoded items
        setForYouItems([
          { brand: 'No Nasties', name: 'Organic Cotton Tee', price: 1299, fabric_score: 90, sustainability_score: 96, image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=300&h=400&fit=crop' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecommendations();
  }, [isAuthenticated]);

  const handleStartScan = () => {
    // Go directly to scan screen with tabs
    setActiveTab('scan');
    setCurrentScreen('scan');
  };

  const handleAddToWishlist = (item) => {
    const wishlistItem = {
      id: `curated-${item.brand.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
      brand: item.brand,
      name: item.name,
      price: item.price,
      imageUrl: item.img,
      tags: ['Curated', 'High-Rated'],
      buyUrl: '#',
      fabric_score: item.fabric,
      sustainability_score: item.sustainability
    };
    
    if (!isInWishlist(wishlistItem.id)) {
      addToWishlist(wishlistItem);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* User Bar */}
      <div className="px-6 pt-4">
        {isAuthenticated ? (
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-black/10 rounded-full flex items-center justify-center">
                <User size={16} className="text-black" />
              </div>
              <div>
                <p className="text-xs font-semibold text-black uppercase">{user?.name}</p>
                <p className="text-[10px] text-black/55">{Math.max(user?.scan_count || 0, scanHistory?.length || 0)} scans</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-black/55 hover:text-black transition-colors"
            >
              <LogOut size={16} />
              <span className="text-xs uppercase">Sign Out</span>
            </button>
          </div>
        ) : (
          <div className="flex justify-end">
            <button
              onClick={openLogin}
              className="flex items-center gap-2 px-4 py-2 text-black/72 hover:text-black hover:bg-black/5 rounded-lg transition-colors"
            >
              <User size={16} />
              <span className="text-xs font-semibold uppercase">Sign In</span>
            </button>
          </div>
        )}
      </div>

      {/* Hero Section */}
      <div className="relative px-6 pt-8 pb-12">
        <div className="relative z-10">
          {/* Logo - Using actual Atelia logo */}
          <div className="flex flex-col items-center mb-12">
            <img 
              src="/logos/logo-wordmark-black.jpg" 
              alt="ATELIA.AI" 
              className="h-16 w-auto object-contain"
            />
          </div>

          {/* Tagline */}
          <div className="mb-10">
            <h2 className="text-4xl leading-[1.1] font-bold text-black mb-4 uppercase tracking-tight text-center">
              The Truth Behind<br />Every Thread.
            </h2>
            <p className="text-black/72 text-base font-normal tracking-wide leading-relaxed uppercase text-center">
              See Beyond the Label.
            </p>
          </div>

          {/* Subtle accent line */}
          <div className="w-12 h-px bg-black/30 mb-10 mx-auto" />

          {/* CTA Button */}
          <button
            onClick={handleStartScan}
            className="group w-full bg-black hover:bg-black/90 text-white py-4 px-8 rounded-2xl
              flex items-center justify-center gap-4 transition-all duration-300"
          >
            <span className="text-base font-bold tracking-[0.15em] uppercase">Begin Scanning</span>
            <ArrowRight
              size={16}
              strokeWidth={2}
              className="transform group-hover:translate-x-0.5 transition-transform duration-300"
            />
          </button>

          {/* Stylist, the conversational two-pane stylist app */}
          <button
            onClick={() => { window.location.href = '/stylist/'; }}
            className="group w-full mt-3 bg-white border-2 border-black/10 hover:border-black/30
              hover:shadow-lg py-3.5 px-6 rounded-2xl flex items-center gap-4 transition-all duration-300"
          >
            <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
              <Sparkles size={18} strokeWidth={2} className="text-white" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-bold tracking-[0.1em] uppercase text-black">Stylist</p>
              <p className="text-black/55 text-xs mt-0.5 leading-snug normal-case tracking-normal">
                Your fabric stylist. Curated picks, scored by Fabric Value Intelligence.
              </p>
            </div>
            <ArrowRight
              size={16}
              strokeWidth={2}
              className="text-black/40 transform group-hover:translate-x-0.5 transition-transform duration-300 flex-shrink-0"
            />
          </button>
        </div>
      </div>

      {/* Feature Cards */}
      <div className="px-6 mt-4">
        <div className="bg-white rounded-3xl p-7 border border-black/12">
          <p className="text-xs uppercase tracking-[0.2em] text-black font-bold mb-5">What You'll Discover</p>
          
          <div className="space-y-6">
            {[
              { title: 'Fabric Intelligence', desc: 'Material quality & comfort analysis', icon: Sparkles },
              { title: 'Sustainability Score', desc: 'Environmental & ethical impact', icon: Leaf },
              { title: 'Smart Alternatives', desc: 'Curated options tailored for you', icon: Layers }
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-4 group">
                <div className="w-10 h-10 bg-black/10 rounded-xl flex items-center justify-center
                  group-hover:bg-black/15 transition-all duration-300 flex-shrink-0">
                  <feature.icon size={18} strokeWidth={2} className="text-black" />
                </div>
                <div className="pt-0.5">
                  <h4 className="font-bold text-black text-base tracking-wide uppercase">
                    {feature.title}
                  </h4>
                  <p className="text-xs text-black/55 mt-1 font-normal">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* High-Rated Items Feed - FOR YOU Section */}
      <div className="px-6 mt-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.2em] text-black font-bold">For You</p>
          <button className="text-xs text-black/55 hover:text-black uppercase font-semibold">
            See All
          </button>
        </div>

        {/* 2x2 Grid - Dynamic from API */}
        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            <div className="col-span-2 text-center py-8">
              <p className="text-black/55 text-sm uppercase">Loading...</p>
            </div>
          ) : forYouItems.length === 0 ? (
            <div className="col-span-2 text-center py-8">
              <p className="text-black/55 text-sm">No recommendations yet</p>
            </div>
          ) : (
            forYouItems.map((item, i) => (
              <div key={i} className="bg-white border border-black/12 rounded-2xl overflow-hidden">
                <div className="aspect-[3/4] bg-black/5 relative">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  <button
                    onClick={() => handleAddToWishlist(item)}
                    className="absolute top-2 right-2 w-8 h-8 bg-white/95 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-all"
                  >
                    <Heart size={16} className="text-black" />
                  </button>
                  <div className="absolute bottom-2 left-2 flex gap-1">
                    <div className="bg-white/95 backdrop-blur px-2 py-1 rounded-lg flex items-center gap-1">
                      <Sparkles size={10} className="text-black" />
                      <span className="text-[10px] font-bold text-black">{item.fabric_score}</span>
                    </div>
                    <div className="bg-white/95 backdrop-blur px-2 py-1 rounded-lg flex items-center gap-1">
                      <Leaf size={10} className="text-black" />
                      <span className="text-[10px] font-bold text-black">{item.sustainability_score}</span>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-[10px] text-black/55 uppercase font-bold">{item.brand}</p>
                  <p className="text-sm font-bold text-black mt-0.5">{item.name}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="px-6 mt-6">
        <div className="bg-black/5 rounded-2xl p-6 text-center">
          <p className="text-black/72 text-xs font-normal tracking-wider uppercase">
            Join the conscious fashion movement
          </p>
          <p className="text-black text-sm font-semibold mt-2 tracking-wide">
            Scan your first garment today
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;